import { User } from '../auth/User';
import { ApiRoutes, getFullApiRoute, replaceRouteParams } from './ApiRoutes';
import { HttpRequest } from './HttpRequest';

export interface ToggleNotificationSettingsPayload {
  notificationsEnabled: boolean;
  id?: string;
}

export interface NotificationPreference {
  id: string;
  notificationId: string;
  locationId: string | null;
  channel: string[];
  trigger: string;
  pluginName?: string;
}

export interface UpdateNotificationPreferencePayload {
  id: string;
  notificationId: string;
  locationId: string | null;
  channel: string[];
  trigger: string;
}

export type NotificationChannelAction = 'enable' | 'disable';

export interface BulkUpdateNotificationPreferenceItem {
  id: string;
  notificationId: string;
  locationId: string | null;
  channel: string;
  action: NotificationChannelAction;
}

export interface BulkUpdateNotificationPreferencesPayload {
  items: BulkUpdateNotificationPreferenceItem[];
}

export interface ResetNotificationPreferencesPayload {
  locationId: string | null;
}

export interface NotificationPreferencesResponse {
  data: NotificationPreference[];
  metadata?: {
    totalCount: number;
    currentCount: number;
    hasPrev: boolean;
    hasNext: boolean;
    page: number;
    perPage: number;
  };
  error?: string;
  stack?: string;
}

export class NotificationsAPI {
  static async getTenantSettings(
    user: User | null,
    tenantId: string,
  ): Promise<{ success: boolean } | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(replaceRouteParams(ApiRoutes.NOTIFICATION_SETTINGS_V3, { tenantId })),
      'GET',
      null,
      null,
      user,
    );
    return result;
  }

  static async toggleTenantSettings(
    user: User | null,
    tenantId: string,
    data: ToggleNotificationSettingsPayload,
  ): Promise<{ success: boolean } | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(replaceRouteParams(ApiRoutes.NOTIFICATION_SETTINGS_TOGGLE_V3, { tenantId })),
      'PUT',
      data,
      null,
      user,
    );
    return result;
  }

  static async getTenantPreferences(
    user: User | null,
    tenantId: string,
    locationId?: string,
    recipientType?: 'dealer' | 'consumer',
  ): Promise<NotificationPreferencesResponse | undefined> {
    let url = getFullApiRoute(
      replaceRouteParams(ApiRoutes.NOTIFICATION_PREFERENCES_V3, { tenantId }),
    );
    const params = new URLSearchParams();
    if (locationId) params.set('locationId', locationId);
    if (recipientType) params.set('recipientType', recipientType);
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result;
  }

  static async updateTenantPreference(
    user: User | null,
    preferenceId: string,
    data: UpdateNotificationPreferencePayload,
  ): Promise<NotificationPreference | undefined> {
    const url = getFullApiRoute(
      replaceRouteParams(ApiRoutes.NOTIFICATION_PREFERENCES_ITEM_V3, { id: preferenceId }),
    );

    const result = await HttpRequest(url, 'PUT', data, null, user);
    return result;
  }

  static async bulkUpdateTenantPreferences(
    user: User | null,
    tenantId: string,
    data: BulkUpdateNotificationPreferencesPayload,
  ): Promise<{ success: boolean } | undefined> {
    const url = getFullApiRoute(
      replaceRouteParams(ApiRoutes.NOTIFICATION_PREFERENCES_BULK_UPDATE_V3, { tenantId }),
    );

    const result = await HttpRequest(url, 'PUT', data, null, user);
    return result;
  }

  static async resetTenantPreferences(
    user: User | null,
    tenantId: string,
    data: ResetNotificationPreferencesPayload,
  ): Promise<{ success: boolean } | undefined> {
    const url = getFullApiRoute(
      replaceRouteParams(ApiRoutes.NOTIFICATION_PREFERENCES_RESET_V3, { tenantId }),
    );

    const result = await HttpRequest(url, 'DELETE', data, null, user);
    return result;
  }
}
